import {
  AutocompleteInputChangeReason,
  Box,
  Chip,
  SxProps,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { RiskLevel } from "../../../../../features/customer/types/queries";
import {
  CustomerOrganization,
  CustomerOrgCompany,
} from "../../../../types/apiTypes";
import AutocompleteGroupedList from "../../AutocompleteGroupedList/AutocompleteGroupedList";
import LoadingSpinner from "../../loadingSpinner/LoadingSpinner";
import AutocompleteBase from "../../fields/autocompleteBase/AutocompleteBase";
import { salesPermitProps } from "./common";

type Props = {
  options: CustomerOrgCompany[];
  value: CustomerOrganization[];
  onInputChange: (value: string, reason: AutocompleteInputChangeReason) => void;
  onChange: (value: CustomerOrganization[] | null) => void;
  disableSalesPermissions?: boolean;
  isLoading: boolean;
  sx?: SxProps;
};

const CustomerOrgCompanyAutocompleteMulti = ({
  options,
  value,
  onInputChange,
  onChange,
  disableSalesPermissions = false,
  isLoading,
  sx,
}: Props) => {
  const formattedOptions: CustomerOrganization[] = useMemo(() => {
    return options.flatMap((company) =>
      company.customerOrganizations.map((org: CustomerOrganization) => ({
        ...org,
      })),
    );
  }, [options]);

  return (
    <AutocompleteBase<CustomerOrganization>
      multiple
      value={value}
      options={formattedOptions}
      loading={isLoading}
      loadingText={<LoadingSpinner />}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      groupBy={(option) => {
        const company = options.find((company) =>
          company.customerOrganizations.some((org) => org.id === option.id),
        );
        return company ? company.name : "";
      }}
      renderGroup={({ group, children }) => (
        <AutocompleteGroupedList group={group} children={children} />
      )}
      renderOption={(props, option) => {
        const customerOrg = options
          .flatMap((company) => company.customerOrganizations)
          .find((org) => org.id === option.id);

        const riskLevel = customerOrg?.salesPermission?.riskLevel;
        const showChip =
          !disableSalesPermissions &&
          riskLevel &&
          riskLevel !== RiskLevel.GREEN;

        return (
          <li {...props}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography>{option.name}</Typography>
              {showChip && (
                <Chip
                  variant="outlined"
                  sx={{ fontWeight: 600, ml: 1 }}
                  {...salesPermitProps[
                    riskLevel as keyof typeof salesPermitProps
                  ]}
                />
              )}
            </Box>
          </li>
        );
      }}
      onInputChange={(_, value, reason) => onInputChange(value, reason)}
      onChange={onChange}
      sx={sx}
    />
  );
};

export default CustomerOrgCompanyAutocompleteMulti;
